import React, { useMemo } from 'react';
import { Alert, Box, Card, CardContent, CardHeader, Chip, Skeleton } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import useApi from '../hooks/useApi';
import { PROGRAMS } from '../constants/programs';
import AnimatedChart from './AnimatedChart';
import CustomLegend from './CustomLegend';

const PROGRAM_COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 159, 64, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(201, 203, 207, 0.7)',
];

const CancelsSummaryCard = ({ onProgramSelect }) => {
  const { useCancelledBacsReport } = useApi();
  const { data, isLoading, error } = useCancelledBacsReport({
    source: 'all',
    includeAppsheet: false,
    limit: 1,
    offset: 0,
  });

  const summary = useMemo(() => data?.summary?.byProgram || {}, [data]);
  const chartData = useMemo(
    () =>
      PROGRAMS.map((program, index) => ({
        id: index,
        label: program,
        value: summary?.[program]?.total || 0,
        color: PROGRAM_COLORS[index % PROGRAM_COLORS.length],
      })),
    [summary]
  );

  const totalCancels = useMemo(
    () => chartData.reduce((sum, item) => sum + (item.value || 0), 0),
    [chartData]
  );

  const handleProgramClick = (programName) => {
    if (!programName || typeof onProgramSelect !== 'function') return;
    onProgramSelect({ programName });
  };

  return (
    <Card sx={{ height: '100%' }} data-testid="cancels-summary-card">
      <CardHeader
        title="Cancels by Program"
        subheader="Cancellation totals"
        action={<Chip size="small" variant="outlined" color="primary" label={`${totalCancels} cancels`} />}
      />
      <CardContent>
        {isLoading && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
              <Skeleton variant="circular" width={140} height={140} />
            </Box>
            <Box sx={{ mt: 1 }}>
              <Skeleton variant="text" width="70%" />
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          </>
        )}
        {error && <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{error.message}</Alert>}
        {!isLoading && !error && totalCancels === 0 && (
          <Alert severity="info">No synthetic cancellations found yet.</Alert>
        )}
        {!isLoading && !error && totalCancels > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
              <AnimatedChart animationKey={`cancels:${chartData.map((d) => d.value).join('-')}`}>
                <PieChart
                  series={[{ data: chartData, innerRadius: 30 }]}
                  width={150}
                  height={150}
                  legend={{ hidden: true }}
                  margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  onClick={(_event, item) => {
                    const label = chartData[item?.dataIndex]?.label;
                    if (label) handleProgramClick(label);
                  }}
                />
              </AnimatedChart>
            </Box>
            <Box sx={{ maxWidth: 220, mx: 'auto', width: '100%' }}>
              <CustomLegend data={chartData} onLegendClick={handleProgramClick} />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CancelsSummaryCard;
